/**
 * Phase 3 — per-message Firestore docs.
 *
 * Replaces the whole-array `users/{uid}/chat_thread_messages/{threadId}` doc
 * (two independent writers, whole-array last-writer-wins, divergent 100/516
 * caps) with one doc per message under
 * `users/{uid}/chat_threads/{threadId}/messages/{messageId}`. Keying by
 * messageId means the client mirror and the server task-completion write are
 * per-id upserts that CANNOT clobber each other's messages.
 *
 * Migration is safe-by-construction: per-message docs are written ADDITIVELY
 * alongside the legacy array doc (which is never deleted), and the read path
 * MERGES both — so even if the per-doc path has a bug, the legacy store remains
 * a complete fallback and no history can be lost.
 *
 * This module is PURE (no Firestore SDK) so the mappers, ordering, id
 * synthesis, and dual-read merge are all unit-testable.
 */
import type { ChatMessage } from '../components/GlobalChatSystem/types';
import { toMessageDoc, fromMessageDoc, capMessageBodyFields, hasInlineDataUrl, stripInlineDataUrls, utf8Bytes, PER_MESSAGE_INLINE_BYTE_BUDGET, PER_MESSAGE_BODY_LIMITS, PER_MESSAGE_DOC_BYTE_BUDGET } from './chatThreadMessagesFirestore';
import { deriveTaskAnchorId } from './taskAnchor';
import { graftRicherAttachments } from './attachmentRichness';
import { PENDING_SOURCE_TAGS, PENDING_ANIMATION_MARKERS } from '../components/GlobalChatSystem/interruptedTurn';

// Re-export so existing call sites can keep importing it from here.
export { deriveTaskAnchorId };

export const PER_MESSAGE_SCHEMA_VERSION = 1 as const;

export interface ChatThreadMessageDocV2 extends Record<string, unknown> {
  /** Stable per-message key (also the Firestore doc id). */
  messageId: string;
  threadId: string;
  instanceId: string;
  /** Epoch millis for chronological ordering (primary sort). */
  orderTs: number;
  /** Insertion-order tiebreaker within an equal orderTs (secondary sort). */
  seq: number;
  /** ISO timestamp of the message (revived to Date on read). */
  timestamp: string;
  schemaVersion: number;
}

// ── stable id synthesis for legacy id-less messages ────────────────────────
// The ConversationHistory persistence path historically dropped `id`, so some
// legacy messages arrive without one. A per-message-doc store MUST key every
// message; synthesize a deterministic id from stable content so re-backfilling
// the same message maps to the same doc (idempotent) rather than duplicating.
function djb2(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) & 0xffffffff;
  }
  return (hash >>> 0).toString(36);
}

type StableIdInput = Partial<Pick<ChatMessage, 'id' | 'isUser' | 'text' | 'html' | 'timestamp' | 'sourceTag'
  | 'messageType' | 'title' | 'attachmentsSerialized' | 'actionButtons' | 'workflowOutput'
  | 'payload'>>;

function synthesizeMessageId(message: StableIdInput, opts: { payloadKind: boolean }): string {
  const tsMs = orderTsForMessage(message as Pick<ChatMessage, 'timestamp'>);
  // COLLISION HARDENING (Phase 3 review Finding 1): hash EVERY distinguishing
  // field, not just a body prefix. Two id-less messages now collide only when
  // they are genuinely identical in content (same role, ts, text, html,
  // sourceTag, type, title, attachment names, action buttons, workflow output)
  // — at which point collapsing them is correct (they are true duplicates,
  // exactly what the reducer's dedupeAdjacentIdenticalAnswers already merges).
  // Empty-body tiles (attachment-only / special tiles) that used to collide on
  // an empty body now differ via their attachment names / title / type.
  const attachSig = Array.isArray(message.attachmentsSerialized)
    ? message.attachmentsSerialized.map((a) => `${a?.name || ''}:${a?.size || 0}`).join(',')
    : '';
  const actionSig = Array.isArray(message.actionButtons)
    ? message.actionButtons.map((b: any) => b?.id || b?.label || '').join(',')
    : '';
  const wfSig = message.workflowOutput
    ? `${(message.workflowOutput as any).type || ''}:${((message.workflowOutput as any).title || '')}`
    : '';
  // PAYLOAD KIND IS BODY (P0 thread-durability, 2026-09-02): two id-less
  // data-only cards (empty text/html — their whole content is the card html
  // and the payload) used to hash identically, so one could take the other's
  // per-doc key. The payload kind separates them and is IMMUTABLE for a given
  // message. Messages that carry an id are untouched (stableMessageId returns
  // the id before ever hashing), so no existing per-doc key churns; the one
  // shape that COULD churn (an id-less payload-bearing legacy row) is bridged
  // by priorSyntheticMessageId in the dual-read merge.
  //
  // The card HTML is deliberately NOT hashed, length included: it is the one
  // field that changes for a message that is otherwise the same row — the
  // whole-array doc's backstop blanks it, the payload rebuild restores it, and
  // the lifecycle status row is appended to it. A mutable term here would mint
  // a SECOND per-doc document for one message and the transcript would render
  // the tile twice. Keys must be a function of identity, never of state.
  const payloadKindSig = message.payload && typeof message.payload === 'object'
    ? String((message.payload as { kind?: unknown }).kind || '')
    : '';
  const parts = [
    message.isUser ? 1 : 0,
    tsMs,
    message.text || '',
    message.html || '',
    message.sourceTag || '',
    (message as any).messageType || '',
    (message as any).title || '',
    attachSig,
    actionSig,
    wfSig,
    // Only when there IS a kind (2026-09-02 review, blocking). Appending an
    // EMPTY term still adds its separator, so the hash moved for every
    // id-less message, not only the payload-bearing ones this term exists
    // for — and priorSyntheticMessageId, which bridges the two keys, returns
    // nothing for a row with no kind. Every id-less legacy row would have
    // re-keyed with no alias behind it and rendered twice.
    ...(opts.payloadKind && payloadKindSig ? [payloadKindSig] : []),
  ].join('\u0000');
  return `synth_${message.isUser ? 'u' : 'a'}_${tsMs}_${djb2(parts)}`;
}

export function stableMessageId(message: StableIdInput): string {
  const existing = typeof message.id === 'string' ? message.id.trim() : '';
  if (existing) return existing;
  return synthesizeMessageId(message, { payloadKind: true });
}

/**
 * The synthetic key this module minted BEFORE the payload-kind term
 * (2026-09-02). An id-less, payload-bearing row hashes differently now, so a
 * per-doc doc written for it under the OLD key would no longer meet its
 * legacy twin in the dual-read union — and the tile would render twice.
 * `buildAnchorAliases` bridges the two keys with this.
 *
 * Returns '' for anything that cannot have churned: a message carrying an id
 * (its key IS the id) or one with no payload kind (same hash either way).
 * Measured population when the term shipped: ZERO id-less payload-bearing
 * rows across two read-only prod scans (the owner's 202 threads / 4,504
 * per-doc messages and a 60-account / 179-thread sweep), so this is a belt
 * for the deploy window and hand-built records, never a repair of known data.
 */
export function priorSyntheticMessageId(message: StableIdInput): string {
  const existing = typeof message.id === 'string' ? message.id.trim() : '';
  if (existing) return '';
  const kind = message.payload && typeof message.payload === 'object'
    ? String((message.payload as { kind?: unknown }).kind || '')
    : '';
  if (!kind) return '';
  return synthesizeMessageId(message, { payloadKind: false });
}

export function orderTsForMessage(message: Pick<ChatMessage, 'timestamp'>): number {
  const t = message.timestamp;
  const ms = t instanceof Date ? t.getTime() : new Date(t as unknown as string).getTime();
  return Number.isFinite(ms) && ms > 0 ? ms : 0;
}

// ── task-answer convergence anchor (Finding 2) ──────────────────────────────
// One logical server-backed answer is written to the per-message-doc store by
// up to THREE writers under DIFFERENT ids: the live client bubble
// (crypto.randomUUID), the client hydrate-recovery write, and the server
// task-completion write (both `prest0n_task_<taskId>`). Keying each doc by
// message.id therefore produced two/three docs for one answer → the dual-read
// union duplicated the report. The fix keys a task-backed answer by a durable
// anchor derived from its serverTaskId, so every writer lands on ONE doc.

/**
 * Surface-plan P2 (2026-08-29): the per-doc id for the Nth ADDITIONAL surface
 * message of one task's turn (a file card, a receipt) delivered as its own
 * message beside the anchored answer. The answer keeps the bare
 * `prest0n_task_<taskId>` anchor untouched — a series sibling can never
 * clobber it. Server twin: functions cardSurface.surfaceSeriesAnchorId —
 * keep the literals in lockstep.
 */
export function surfaceSeriesDocId(parentAnchorId: string, order: number): string {
  const n = Number.isFinite(order) && order > 0 ? Math.floor(order) : 1;
  return `${parentAnchorId}__s${n}`;
}

/**
 * The per-message-doc key (and dual-read merge key) for a message.
 * Precedence: an already-frozen `taskAnchorId` → an anchor derived from a live
 * `serverTaskId` (belt-and-suspenders for a message persisted mid-stream before
 * the reducer froze the anchor) → the stable message id. A message with neither
 * keys by its own id, exactly as pre-convergence.
 */
export function perDocMessageId(
  message: Partial<Pick<ChatMessage, 'id' | 'isUser' | 'text' | 'html' | 'timestamp' | 'sourceTag'
    | 'messageType' | 'title' | 'attachmentsSerialized' | 'actionButtons' | 'workflowOutput'
    | 'payload' | 'serverTaskId' | 'taskAnchorId'>>,
): string {
  const frozen = typeof message.taskAnchorId === 'string' ? message.taskAnchorId.trim() : '';
  if (frozen) return frozen;
  const live = deriveTaskAnchorId(message.serverTaskId);
  if (live) return live;
  return stableMessageId(message);
}

/**
 * Serialize a ChatMessage into a per-message doc. `seq` should be the message's
 * position in the source array at write time (stable insertion-order tiebreaker
 * — chat is append-only, and finalize updates in place by id).
 */
export function toMessageDocV2(
  message: ChatMessage,
  threadId: string,
  instanceId: string,
  seq: number,
): ChatThreadMessageDocV2 {
  // BOUNDS (INCIDENT-20260822): this lane had NO size handling at
  // all — the per-message doc carried the triple body AND both attachments as
  // inline base64, so Firestore rejected it (and, batched, sank its
  // batch-mates). Inline dataUrls strip when the doc exceeds the shared inline
  // budget (small inline attachments still persist; durable Storage locators
  // always survive). The BODY caps are this lane's own — see below.
  // A PER-MESSAGE DOC OWNS ITS OWN ~1 MiB (2026-09-18). This lane used to apply
  // the SHARED-document caps — 20 000 chars of text for a document nothing else
  // shares — so a Deep Research report was stored cut off, with the
  // truncation marker, beside the server's own uncapped copy of the same
  // answer. The budget here is the one the server's payload slimmer already
  // uses, so the two writers agree.
  let bounded = capMessageBodyFields(toMessageDoc(message), PER_MESSAGE_BODY_LIMITS);
  if (hasInlineDataUrl(bounded) && utf8Bytes(bounded) > PER_MESSAGE_INLINE_BYTE_BUDGET) {
    bounded = stripInlineDataUrls(bounded);
  }
  // Belt: Firestore refuses a doc over 1 MiB. Anything still oversized after the
  // generous caps and the inline strip falls back to the shared-lane numbers —
  // a truncated body persists, a rejected write does not.
  if (utf8Bytes(bounded) > PER_MESSAGE_DOC_BYTE_BUDGET) {
    bounded = capMessageBodyFields(bounded);
  }
  const base = bounded as Record<string, unknown>;
  // CONVERGENCE (Finding 2): key a task-backed answer by its durable anchor so
  // the client bubble and the server completion write land on ONE doc. Persist
  // the anchor into the body too, so the revived message carries it and the
  // dual-read merge key converges with the legacy copy (whose own id differs).
  const anchor = perDocMessageId(message);
  const isAnchored = anchor !== stableMessageId(message);
  const messageId = anchor;
  return {
    ...base,
    id: messageId,
    messageId,
    ...(isAnchored ? { taskAnchorId: anchor } : {}),
    threadId,
    instanceId,
    // HONOUR THE KEY THE SEND PATH ALREADY MINTED (owner report 2026-09-17).
    // This used to be `orderTsForMessage(message)` unconditionally, and the
    // explicit key overrides the spread of `base` — so `message.orderTs`, which
    // the send path mints from the task id for exactly this purpose
    // (useGlobalChat's sendOrderTs), never reached the document. A carried
    // stamp is the turn's own mint instant, which is what the SERVER writes the
    // answer under too (taskQueue.deterministicMessageAnchorForTask), so the
    // pair ties here and `seq` resolves it. Absent ⇒ the timestamp, exactly as
    // before, so every message ever written keeps the position it has.
    orderTs: typeof message.orderTs === 'number' && Number.isFinite(message.orderTs) && message.orderTs > 0
      ? message.orderTs
      : orderTsForMessage(message),
    // Likewise `seq`. The `seq` PARAMETER is the message's index in the array
    // being written, which is monotonic but differs per store (the client
    // writes the whole array; the server writes one doc with mergedLength-1).
    // A question carries `seq: 0` from the send path — a fact about its
    // position IN ITS TURN, not in an array — so honouring it makes the
    // question's stamp identical whichever store a reader gets it from, which
    // is what the tie-break in mergeLegacyAndPerDoc needs. Absent ⇒ the array
    // index, exactly as before.
    seq: typeof message.seq === 'number' && Number.isFinite(message.seq) ? message.seq : seq,
    timestamp: typeof base.timestamp === 'string' ? base.timestamp : new Date(0).toISOString(),
    schemaVersion: PER_MESSAGE_SCHEMA_VERSION,
  };
}

/** Deserialize + order a set of per-message docs into a ChatMessage[]. */
export function fromMessageDocsV2(docs: ChatThreadMessageDocV2[]): ChatMessage[] {
  if (!Array.isArray(docs) || docs.length === 0) return [];
  const ordered = docs
    .filter((d) => d && typeof d.messageId === 'string')
    .slice()
    .sort((a, b) => {
      const at = typeof a.orderTs === 'number' ? a.orderTs : 0;
      const bt = typeof b.orderTs === 'number' ? b.orderTs : 0;
      if (at !== bt) return at - bt;
      const as = typeof a.seq === 'number' ? a.seq : 0;
      const bs = typeof b.seq === 'number' ? b.seq : 0;
      return as - bs;
    });
  return ordered.map((d) => fromMessageDoc(d));
}

/**
 * Finding 4 (downgrade guard). Once writers converge on ONE anchored doc, a
 * still-streaming or interrupted copy of a task answer would `merge:true`
 * OVERWRITE the finalized doc's body with a partial/empty one. Gate the
 * per-message write so only SETTLED copies are persisted:
 *  - user messages are always settled (never stream);
 *  - an assistant message is settled only when it has a real body AND is not
 *    actively streaming/pending AND not interrupted.
 * A never-finalized answer therefore never reaches the per-doc store; it still
 * lives in the authoritative legacy array and surfaces via the dual-read union,
 * and its finalized replacement (client or server) writes the anchored doc.
 */
export function isSettledForPerDoc(message: ChatMessage): boolean {
  if (message?.isUser) return true;
  if (message?.interrupted || message?.status === 'interrupted') return false;
  if (message?.status === 'streaming') return false;
  // A LIVE pending / deep-research stage widget carries a body (the spinner html
  // and a 'thinking…' text) but NO 'streaming' status — it is marked by a
  // pending sourceTag or pending-animation html, exactly as the rest of the
  // codebase detects it. It shares the answer's anchor key (serverTaskId is
  // stamped onto it early), so persisting it would let a stale spinner clobber
  // the finalized report on the anchored doc (adversarial review, HIGH). Never
  // write it; finalize strips the pending sourceTag, so the real report — which
  // carries no spinner markers — is never excluded by this guard.
  const sourceTag = typeof message?.sourceTag === 'string' ? message.sourceTag : '';
  if (sourceTag && PENDING_SOURCE_TAGS.has(sourceTag)) return false;
  const html = typeof message?.html === 'string' ? message.html : '';
  if (html && PENDING_ANIMATION_MARKERS.some((mark) => html.includes(mark))) return false;
  // Data-only cards (2026-08-22 owner report: connect cards vanished on
  // return): renderPolicy:'never' messages carry their ENTIRE content in
  // workflowActionButtonsHtml + payload with an empty body — that IS a real,
  // finalized message and must reach the cloud store at its position.
  const cardHtml = typeof message?.workflowActionButtonsHtml === 'string'
    ? message.workflowActionButtonsHtml
    : '';
  const hasBody = !!((message?.text || '').trim() || html.trim() || cardHtml.trim());
  return hasBody;
}

/**
 * Compute the set of per-message docs to upsert for a thread's current message
 * array. Assigns a stable `seq` from array position. UNCAPPED (Miles
 * 2026-07-12): the per-message store is the thread's FULL history — each doc is
 * an idempotent per-id upsert, so re-persisting an unchanged message converges
 * on the same doc. Retention bounding lives only on the legacy whole-array doc
 * (a recent-window cache); write-amplification is bounded by the repository's
 * session fingerprint cache, which skips docs already written unchanged.
 */
export function buildMessageDocsForThread(
  messages: ChatMessage[],
  threadId: string,
  instanceId: string,
): ChatThreadMessageDocV2[] {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  return messages.map((m, i) => toMessageDocV2(m, threadId, instanceId, i));
}

// ── dual-read merge ─────────────────────────────────────────────────────────
function messageKey(m: ChatMessage): string {
  return perDocMessageId(m);
}

/**
 * Build an alias map from any legacy message that carries a convergence anchor:
 * its own stable id (e.g. the client bubble's `crypto.randomUUID`) → its anchor
 * (`prest0n_task_<taskId>`). This lets the merge collapse an ORPHAN per-doc doc
 * written under the bubble id BEFORE the convergence fix shipped (no anchor in
 * its body) with the anchored doc written by the server — the legacy copy is
 * the bridge that proves they are the same logical answer. Without this, a
 * task answer persisted in the deploy gap could still duplicate on read.
 */
function buildAnchorAliases(rows: ChatMessage[]): Map<string, string> {
  const alias = new Map<string, string>();
  for (const m of rows) {
    if (!m) continue;
    const anchor = typeof m.taskAnchorId === 'string' ? m.taskAnchorId.trim() : '';
    const ownId = stableMessageId(m);
    if (anchor && ownId && ownId !== anchor) alias.set(ownId, anchor);
    // PAYLOAD-KIND KEY BELT (2026-09-02): stableMessageId now hashes
    // payload.kind, so an id-less payload-bearing legacy row keys differently
    // than the per-doc doc written for it before that shipped. Bridge the old
    // key onto today's so the two copies collapse into ONE entry instead of
    // rendering the tile twice — the deploy-window case (an old tab writing
    // the old key while a new tab computes the new one) included.
    const prior = priorSyntheticMessageId(m);
    const target = anchor || ownId;
    if (prior && target && prior !== target) alias.set(prior, target);
  }
  return alias;
}

/** The card html a data-only message carries as its whole content. */
function cardHtmlOf(m: ChatMessage): string {
  return typeof m.workflowActionButtonsHtml === 'string' ? m.workflowActionButtonsHtml.trim() : '';
}

function completionRank(m: ChatMessage): number {
  if (m.status === 'complete' && !m.interrupted) return 4;
  // CARD HTML IS BODY (P0 thread-durability, 2026-09-02): a data-only card
  // with its html is a finalized message; the same card with its html
  // blanked by the whole-array doc's backstop is a poorer copy of it. The
  // intact copy must outrank the stripped one in EITHER consideration order
  // (repository reads consider per-doc first; loadEarlierMessages hands the
  // local copy in as "legacy"). Streaming/interrupted copies carry no card
  // html, so the never-downgrade invariants are untouched.
  const hasBody = !!((m.text || '').trim() || (m.html || '').trim() || cardHtmlOf(m));
  if (hasBody && !m.interrupted && m.status !== 'interrupted') return 3;
  if (m.status === 'interrupted' || m.interrupted) return 1;
  return 2;
}

/**
 * Tie-break signal for two copies of EQUAL completionRank. Prefers the copy that
 * carries more first-class content — token usage, workflow output, action
 * buttons, attachments — and, failing that, the longer body. This makes the
 * merge pick the field-richer copy REGARDLESS of consideration order, so a
 * transiently-corrupt or field-stripped copy can never mask the real answer on a
 * rank tie (defense-in-depth for the adversarial-review findings). Metadata
 * dominates; body length is a fine-grained secondary tiebreak.
 */
function richnessScore(m: ChatMessage): number {
  const anyM = m as unknown as Record<string, unknown>;
  let meta = 0;
  // 2026-08-16 (aux-usage fold): STEP COUNT, not presence — a server copy
  // whose ⓘ gained a sidecar step (memory update / follow-up suggestions)
  // must beat the client copy holding one step fewer; the old boolean tied
  // them and the body-length tiebreak could silently drop the appended step.
  if (Array.isArray(anyM.tokenUsages) && (anyM.tokenUsages as unknown[]).length > 0) {
    meta += 1 + Math.min((anyM.tokenUsages as unknown[]).length, 40);
  } else if (anyM.tokenUsages || anyM.tokenUsage) {
    meta += 1;
  }
  if (anyM.workflowOutput) meta += 1;
  if (Array.isArray(anyM.actionButtons) && (anyM.actionButtons as unknown[]).length) meta += 1;
  if (Array.isArray(anyM.attachmentsSerialized) && (anyM.attachmentsSerialized as unknown[]).length) meta += 1;
  // CARD HTML + PAYLOAD ARE BODY (P0 thread-durability, 2026-09-02): a
  // structured payload is first-class content, and the card html counts
  // toward body length — so an intact card copy beats the SAME card with its
  // html blanked (they used to tie, and the tie went to whichever store was
  // considered first).
  const payload = anyM.payload as { kind?: unknown } | undefined;
  if (payload && typeof payload === 'object' && typeof payload.kind === 'string' && payload.kind) meta += 1;
  const bodyLen = (m.text || '').trim().length + (m.html || '').trim().length + cardHtmlOf(m).length;
  return meta * 100000 + Math.min(bodyLen, 99999);
}

// attachmentLocatorRichness / graftRicherAttachments moved to ./attachmentRichness
// (2026-09-08) so the group-thread overlay merge applies the same never-
// downgrade rule; behaviour here is unchanged.

/**
 * Merge the legacy whole-array copy with the per-message-doc copy for dual-read.
 * Per-doc is the forward store, but during migration the legacy array may hold
 * messages not yet backfilled (or a richer copy). Rules:
 *  - union by stable message id;
 *  - when both stores have the same id, keep the MORE COMPLETE copy (a finalized
 *    per-doc answer beats a legacy pending placeholder and vice-versa) — this is
 *    the never-downgrade invariant applied across stores;
 *  - order the result chronologically (timestamp, then `seq`, then original
 *    relative order — see the comparator's own note on why `seq` sits above
 *    the first-seen index).
 * Pure; returns [] for two empty inputs.
 */
export function mergeLegacyAndPerDoc(legacy: ChatMessage[], perDoc: ChatMessage[]): ChatMessage[] {
  const legacyArr = Array.isArray(legacy) ? legacy : [];
  const perDocArr = Array.isArray(perDoc) ? perDoc : [];
  if (legacyArr.length === 0) return perDocArr;
  if (perDocArr.length === 0) return legacyArr;

  // The legacy copy of a task answer carries BOTH its own bubble id and the
  // anchor, so it can bridge an orphan per-doc doc (bubble-id keyed, pre-fix) to
  // the anchored doc. Resolve every key through this alias so all copies of one
  // logical answer collapse to a single entry.
  // Both arrays feed the alias map so the union is ORDER-INDEPENDENT: the
  // bridging row (the copy that carries both keys) can arrive from either
  // store depending on the call site (the dual read hands the legacy array
  // first; loadEarlierMessages hands the local pane in as "legacy").
  const alias = buildAnchorAliases(legacyArr.concat(perDocArr));
  const keyOf = (m: ChatMessage): string => {
    const k = messageKey(m);
    return alias.get(k) || k;
  };

  const order = new Map<string, number>();
  const chosen = new Map<string, ChatMessage>();
  let seq = 0;
  const consider = (m: ChatMessage) => {
    if (!m) return;
    const key = keyOf(m);
    if (!order.has(key)) order.set(key, seq++);
    const prev = chosen.get(key);
    if (!prev) {
      chosen.set(key, m);
      return;
    }
    const rankM = completionRank(m);
    const rankP = completionRank(prev);
    // Higher completion wins; on a tie, the field-richer copy wins (never-
    // downgrade extended to content richness, order-independently).
    if (rankM > rankP || (rankM === rankP && richnessScore(m) > richnessScore(prev))) {
      chosen.set(key, graftRicherAttachments(m, prev));
    } else {
      chosen.set(key, graftRicherAttachments(prev, m));
    }
  };
  // Per-doc first so its ordering seeds `order`; legacy fills gaps.
  perDocArr.forEach(consider);
  legacyArr.forEach(consider);

  const tsMs = (m: ChatMessage): number => orderTsForMessage(m);
  const seqOf = (m: ChatMessage): number | undefined =>
    typeof m?.seq === 'number' && Number.isFinite(m.seq) ? m.seq : undefined;
  return Array.from(chosen.values()).sort((a, b) => {
    const at = tsMs(a);
    const bt = tsMs(b);
    if (at !== bt) return at - bt;
    // EQUAL INSTANTS ARE A TURN, NOT A COINCIDENCE (owner report 2026-09-17).
    // A question and its answer now deliberately share the turn's mint instant
    // (see useGlobalChat's sendOrderTs and toMessageDocV2 above), so the tie is
    // the normal case and must resolve the same way on every device. `seq` is
    // the writers' own answer to it — 0 for the question, higher for what
    // followed — and `fromMessageDocsV2` has always broken this tie on `seq`;
    // this makes the two comparators agree. The first-seen index below is
    // DEVICE-LOCAL (it depends on which store fed the merge first), which is
    // why it can never be the authority for a pair that ties by design.
    //
    // Inert on history: before today a question stamped `new Date()` a few ms
    // after its answer's mint, so an existing pair does not tie and never
    // reaches this branch. Where old rows do tie to the millisecond they were
    // already ordered device-locally, so `seq` only makes them deterministic.
    const as = seqOf(a);
    const bs = seqOf(b);
    if (as !== undefined && bs !== undefined && as !== bs) return as - bs;
    // `seq` can be missing on one side (the legacy array copy of a
    // server-written answer carries whatever its caller built), and a
    // tie-break that needs a field to be present is a tie-break that
    // quietly stops working. Within one instant a person's message precedes
    // the reply to it — true of a question/answer pair by construction, and
    // silent for two messages of the same kind, which fall through.
    if (a.isUser !== b.isUser) return a.isUser ? -1 : 1;
    return (order.get(keyOf(a)) ?? 0) - (order.get(keyOf(b)) ?? 0);
  });
}
